import React, { ReactNode, useState, useRef, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  OutlinedInput,
  SelectChangeEvent,
  SxProps,
  Theme,
  Checkbox,
  ListItemText,
  Button,
  Divider,
  styled,
  Popper,
  ClickAwayListener,
  Paper,
  List
} from '@mui/material';

const StyledFormControlContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  overflow: 'hidden',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    }
  }
}));

export interface DropdownOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export interface StyledDropdownProps<T extends string | string[]> {
  label: string;
  name: string;
  value: T;
  onChange: (event: SelectChangeEvent<T>) => void;
  options: DropdownOption[];
  helperText?: string;
  multiple?: boolean;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
  margin?: 'none' | 'dense' | 'normal';
  slotProps?: {
    inputLabel?: {
      sx?: SxProps<Theme>;
    };
    [key: string]: any;
  };
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  'data-id'?: string;
  startAdornment?: React.ReactNode;
  [key: string]: any; // For any additional props
}

export const StyledDropdown = <T extends string | string[]>({
  label,
  name,
  value,
  onChange,
  options = [],
  helperText,
  multiple = false,
  fullWidth = true,
  margin = 'normal',
  sx = {},
  slotProps = {},
  disabled = false,
  error = false,
  required = false,
  startAdornment,
  ...rest
}: StyledDropdownProps<T>): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement | null>(null);
  const [selectWidth, setSelectWidth] = useState<number>(0);
  const [selectPosition, setSelectPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      setSelectWidth(rect.width);
      setSelectPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [open]);

  const getOptionLabel = (optionValue: string): string => {
    const option = options.find(opt => opt.value === optionValue);
    return option ? option.label : '';
  };

  const inputLabelStyles: SxProps<Theme> = {
    fontSize: '0.65rem',
    lineHeight: 1.2,
    whiteSpace: 'normal',
    overflow: 'visible',
    maxWidth: 'none',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.65)',
    },
    '@media (max-width: 600px)': {
      fontSize: '0.6rem',
      lineHeight: 1.1,
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.6)',
      },
    },
    ...(slotProps?.inputLabel?.sx || {})
  };

  const isItemSelected = (optionValue: string): boolean => {
    if (multiple) {
      return (value as string[]).indexOf(optionValue) > -1;
    }
    return value === optionValue;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleItemClick = (optionValue: string) => {
    if (multiple) {
      const currentValues = value as string[];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];
      
      const selectEvent = {
        target: {
          name: name,
          value: newValues
        }
      } as SelectChangeEvent<T>;
      
      onChange(selectEvent);
    } else {
      const selectEvent = {
        target: {
          name: name,
          value: optionValue
        }
      } as SelectChangeEvent<T>;
      
      onChange(selectEvent);
      handleClose();
    }
  };

  const renderValue = () => {
    if (multiple) {
      const selectedArray = value as string[];
      if (selectedArray.length === 0) return '';
      
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedArray.map((val) => (
            <Chip
              key={val}
              label={getOptionLabel(val)}
              size="small"
              sx={{ fontSize: '0.75rem' }}
            />
          ))}
        </Box>
      );
    }
    return getOptionLabel(value as string);
  };

  return (
    <StyledFormControlContainer sx={sx}>
      <FormControl
        fullWidth={fullWidth}
        margin={margin}
        variant="outlined"
        disabled={disabled}
        error={error}
        required={required}
        ref={selectRef}
      >
        <InputLabel
          id={`${name}-label`}
          sx={inputLabelStyles}
        >
          {label}
        </InputLabel>
        <Select
          labelId={`${name}-label`}
          id={name}
          name={name}
          value={value}
          input={
            <OutlinedInput 
              label={label} 
              startAdornment={startAdornment} 
            />
          }
          data-id={rest['data-id']}
          renderValue={renderValue}
          open={false}
          onClick={() => {
            if (!disabled) {
              setOpen(!open);
            }
          }}
          IconComponent={(props) => (
            <Box 
              component="span" 
              {...props} 
              sx={{ 
                pointerEvents: 'none',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            >
              ▼
            </Box>
          )}
          MenuProps={{ 
            open: false
          }}
          {...rest}
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>

      {open && (
        <Popper
          open={open}
          anchorEl={selectRef.current}
          placement="bottom-start"
          style={{
            zIndex: 1500,
            width: selectWidth, 
            position: 'fixed',
            top: selectPosition.top + 'px',
            left: selectPosition.left + 'px'
          }}
          modifiers={[
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: true,
                boundary: 'viewport'
              }
            }
          ]}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              elevation={3}
              sx={{
                maxHeight: 300,
                overflow: 'auto',
                borderRadius: 2
              }}
            >
              <List sx={{ padding: 0 }}>
                {options.map((option) => (
                  <StyledMenuItem
                    key={option.value}
                    selected={isItemSelected(option.value)}
                    onClick={() => handleItemClick(option.value)}
                  >
                    {multiple && (
                      <Checkbox
                        checked={isItemSelected(option.value)}
                        sx={{ padding: '4px', marginRight: '8px' }}
                      />
                    )}
                    
                    {option.icon && (
                      <Box component="span" sx={{ mr: 1.5, verticalAlign: 'middle' }}>
                        {option.icon}
                      </Box>
                    )}
                    
                    <ListItemText primary={option.label} />
                  </StyledMenuItem>
                ))}

                {multiple && (
                  <>
                    <Divider />
                    <Box 
                      sx={{ 
                        padding: '8px 16px', 
                        textAlign: 'center'
                      }}
                    >
                      <Button 
                        variant="contained" 
                        color="primary"
                        size="small"
                        onClick={handleClose}
                        sx={{ minWidth: '100px' }}
                      >
                        Done
                      </Button>
                    </Box>
                  </>
                )}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </StyledFormControlContainer>
  );
};